import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ViewInstance, ViewProps } from 'react-native';
import { Animated, Platform } from 'react-native';

import { useReduceMotionStatus } from '../../hooks';

export const FadeView = React.forwardRef<ViewInstance, FadeViewProps>(
  ({ show, duration = 200, style, children, keepMounted, ...restProps }, ref) => {
    const [shouldRenderChildren, setShouldRenderChildren] = useState(show);
    const reduceMotionStatus = useReduceMotionStatus();

    // ─────────────────────────────────────────────────────────────────────

    const opacity = useRef(new Animated.Value(0)).current;
    const nativeDriverConfig = Platform.OS !== 'web';

    // ─── Refs ────────────────────────────────────────────────────────────

    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    const showAnimationRef = useRef<Animated.CompositeAnimation>();
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    const hideAnimationRef = useRef<Animated.CompositeAnimation>();

    // ─── Helpers ─────────────────────────────────────────────────────────

    const showChildren = useCallback(() => {
      if (reduceMotionStatus === 'undetermined') return;

      setShouldRenderChildren(true);

      hideAnimationRef.current?.reset(); // reset a possible stale animation
      showAnimationRef.current = Animated.timing(opacity, {
        toValue: 1,
        duration: reduceMotionStatus === 'disabled' ? duration : 0,
        useNativeDriver: nativeDriverConfig,
      });

      showAnimationRef.current.start();
    }, [duration, nativeDriverConfig, opacity, reduceMotionStatus]);

    const hideChildren = useCallback(() => {
      if (reduceMotionStatus === 'undetermined') return;

      hideAnimationRef.current = Animated.timing(opacity, {
        toValue: 0,
        duration: reduceMotionStatus === 'disabled' ? duration : 0,
        useNativeDriver: nativeDriverConfig,
      });

      hideAnimationRef.current.start(({ finished }) => {
        // unmount component if the animation was not interrupted...
        if (finished) {
          setShouldRenderChildren(false);
        }

        // ...otherwise reset the animation's initial state
        else {
          showChildren();
        }
      });
    }, [duration, nativeDriverConfig, opacity, reduceMotionStatus, showChildren]);

    // ─── Effects ─────────────────────────────────────────────────────────

    useEffect(() => {
      if (show) {
        showChildren();
      } else {
        hideChildren();
      }
    }, [hideChildren, show, showChildren]);

    // ─────────────────────────────────────────────────────────────────────

    return shouldRenderChildren || keepMounted ? (
      <Animated.View ref={ref} style={[style, { opacity }]} {...restProps}>
        {children}
      </Animated.View>
    ) : null;
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

type FadeViewProps = Readonly<{
  children?: ReactNode;
  show: boolean;
  duration?: number;
  keepMounted?: boolean;
}> &
  ViewProps;
